import {
  Badge,
  Box,
  ButtonSelect,
  Link,
  Loader,
  type MenuMultiOption,
  type MenuOption,
} from "@saviynt/design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import PaginatedFilterMenu from "../PaginatedFilterMenu/PaginatedFilterMenu";
import { useAssignOwnerFilter } from "./AssignOwnerFilterContext";
import {
  ALL_FILTER_CHIPS,
  DEFAULT_FILTER_CHIPS_TO_SHOW,
} from "../../../../../../../../constants/policies.constants";
import AddOrRemoveFilter from "../../../../../../../../components/AddOrRemoveFilter/AddOrRemoveFilter";
import { extractMultiSelectOptions } from "../../utils";
import { useAssignOwnerFiltersList } from "../../../../../../../../hooks/useAssignOwners";

export interface AddFilterOption {
  label: string;
  value: string;
  isDisabled: boolean;
  isChecked: boolean;
}

interface AssignOwnerFilterOptionsProps {
  /**
   * Controls how many filter chips to display initially.
   * @param maxVisibleFilters - Number of filters to show, 'all' to show all filters, or undefined for default
   * @default DEFAULT_FILTER_CHIPS_TO_SHOW when undefined
   * @example
   * // Show default number of filters (from constant)
   * <AssignOwnerFilterOptions />
   *
   * // Show specific number of filters
   * <AssignOwnerFilterOptions maxVisibleFilters={5} />
   *
   * // Show all filters without slicing
   * <AssignOwnerFilterOptions maxVisibleFilters="all" />
   */
  maxVisibleFilters?: number | "all";
}

export default function AssignOwnerFilterOptions({
  maxVisibleFilters,
}: AssignOwnerFilterOptionsProps) {
  const intl = useIntl();
  const { filtersData, isLoading, isError } = useAssignOwnerFiltersList();
  const [visibleFilters, setVisibleFilters] = useState<string[]>([]);
  const { selectedFilters, setSelectedFilters } = useAssignOwnerFilter();

  useEffect(() => {
    if (filtersData.length > 0 && !isLoading && !isError) {
      // Apply slicing based on maxVisibleFilters parameter
      const slicedData =
        maxVisibleFilters === ALL_FILTER_CHIPS
          ? filtersData
          : filtersData.slice(
              0,
              maxVisibleFilters ?? DEFAULT_FILTER_CHIPS_TO_SHOW,
            );

      setVisibleFilters(
        slicedData
          .map((f) => f.field)
          .filter((f): f is string => typeof f === "string"),
      );
    }
  }, [filtersData, isLoading, maxVisibleFilters, isError]);

  const handleFilterValueChange =
    (field: string) =>
    (selectedOptions?: MenuMultiOption[] | MenuMultiOption) => {
      const options = extractMultiSelectOptions(selectedOptions);
      const values = options.map((option) => option.value);

      setSelectedFilters((prev) => {
        const updated = {
          ...prev,
          [field]: values,
        };
        return updated;
      });
    };

  const handleClearAll = () => {
    setSelectedFilters({});
    if (filtersData.length > 0) {
      const slicedData =
        maxVisibleFilters === "all"
          ? filtersData
          : filtersData.slice(
              0,
              maxVisibleFilters ?? DEFAULT_FILTER_CHIPS_TO_SHOW,
            );

      setVisibleFilters(
        slicedData
          .map((f) => f.field)
          .filter((f): f is string => typeof f === "string"),
      );
    }
  };

  const addFilterOptions = useMemo(
    () =>
      filtersData.map(
        ({ name = "", field = "", show }) =>
          ({
            label: name,
            value: field,
            isDisabled: !show,
            isChecked: visibleFilters.includes(field ?? ""),
            category: "Filter Group",
          }) as MenuOption,
      ),
    [filtersData, visibleFilters],
  );

  const handleAddFilterChange = useCallback(
    (selectedOption: AddFilterOption) => {
      setVisibleFilters((currentFilters) => {
        if (currentFilters.includes(selectedOption.value)) {
          return currentFilters.filter((f) => f !== selectedOption.value);
        }

        return [...currentFilters, selectedOption.value];
      });
    },
    [],
  );

  const filterConfigs = visibleFilters.map((key) => {
    const found = filtersData.find((f) => f.field === key);

    return {
      label: found?.name ?? key,
      key,
      show: found?.show,
    };
  });

  if (isLoading)
    return (
      <Box className="flex gap-4 my-4 items-center">
        <Loader kind="dots" format="inline" color="brand" size="medium" />
      </Box>
    );

  return (
    <Box className="flex gap-4 my-4 items-center">
      {filterConfigs.map(({ label, key, show }) => {
        if (!show) {
          return null;
        }

        const selectedValues = selectedFilters[key] || [];
        return (
          <PaginatedFilterMenu
            key={key}
            field={key}
            label={label}
            isSingleSelect={false}
            selectedValues={selectedValues}
            onChange={handleFilterValueChange(key)}
            trigger={
              <ButtonSelect
                kind="pill"
                size="medium"
                label={label}
                BadgeComp={
                  selectedValues.length > 0 ? (
                    <Badge kind="dot" color="primary" size="small" />
                  ) : null
                }
              />
            }
          />
        );
      })}
      {maxVisibleFilters !== ALL_FILTER_CHIPS &&
        filtersData.length >
          (maxVisibleFilters ?? DEFAULT_FILTER_CHIPS_TO_SHOW) && (
          <AddOrRemoveFilter
            dataTestId="FilterToolbar-addOrRemove"
            addFilterOptions={addFilterOptions}
            onAddFilterChange={handleAddFilterChange}
          />
        )}
      <Link
        className="NHI-Dashboard-ClearFilters"
        text={intl.formatMessage({
          defaultMessage: "Clear",
          id: "3E8xVq",
          description: "Button text to clear date filter selection",
        })}
        kind="button"
        onClick={handleClearAll}
      />
    </Box>
  );
}
